import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Avatar } from '@/components/Chips'
import { Icon } from '@/components/Icon'
import {
  deleteDocument,
  documentUrl,
  fetchDocuments,
  updateDocument,
  uploadDocuments,
  type ProjectDocument,
  type RejectedFile,
  type UploadResult,
} from '@/features/documents/api'
import type { ApiError } from '@/lib/api/client'
import { formatSize } from '@/features/documents/size'
import type { Visibility } from '@/types/domain'

/**
 * The project's document library.
 *
 * Files arrive in handfuls — someone drags in the folder they were sent — so
 * the whole surface is a drop target and the result of a drop is reported per
 * file. A batch where one file was refused still added the rest, and saying so
 * plainly is the difference between a library people fill and one they give up
 * on.
 *
 * Each document carries its own conversation, opened in a panel over this list
 * rather than instead of it.
 */
export function DocumentLibrary({
  projectKey,
  onOpen,
}: {
  projectKey: string
  onOpen: (document: ProjectDocument) => void
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const fileInput = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const [visibility, setVisibility] = useState<Visibility>('internal')
  const [result, setResult] = useState<UploadResult | null>(null)

  const documents = useQuery<ProjectDocument[], ApiError>({
    queryKey: ['documents', projectKey],
    queryFn: () => fetchDocuments(projectKey),
    retry: false,
  })

  const upload = useMutation<UploadResult, ApiError, File[]>({
    mutationFn: (files) => uploadDocuments(projectKey, files, visibility),
    onSuccess: (uploaded) => {
      setResult(uploaded)
      void queryClient.invalidateQueries({ queryKey: ['documents', projectKey] })
    },
  })

  function send(files: FileList | null) {
    const chosen = Array.from(files ?? [])
    if (chosen.length > 0) upload.mutate(chosen)
  }

  return (
    <section
      onDragOver={(event) => {
        event.preventDefault()
        setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault()
        setDragging(false)
        send(event.dataTransfer.files)
      }}
      className={`rounded-md border-2 border-dashed transition-colors ${
        dragging ? 'border-brand-300 bg-brand-50/60' : 'border-transparent'
      }`}
    >
      <header className="mb-3 flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-ink-900 text-sm font-semibold">{t('document.title')}</h2>
          <p className="text-ink-500 text-[12px]">{t('document.subtitle')}</p>
        </div>

        <div className="flex items-center gap-1">
          {(['internal', 'client'] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setVisibility(option)}
              aria-pressed={visibility === option}
              className={`rounded px-2 py-0.5 font-mono text-[10px] tracking-wide uppercase ${
                visibility === option
                  ? option === 'client'
                    ? 'bg-brand-600 text-white'
                    : 'bg-ink-900 text-white'
                  : 'text-ink-500 hover:bg-surface-2'
              }`}
            >
              {option === 'client' ? t('view.clientVisible') : t('view.internal')}
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          disabled={upload.isPending}
          className="bg-brand-600 flex items-center gap-1.5 rounded px-3 py-1.5 text-[12px] font-medium text-white disabled:opacity-40"
        >
          <Icon name="upload" className="size-3.5" />
          {t('document.choose')}
        </button>

        <input
          ref={fileInput}
          type="file"
          multiple
          hidden
          onChange={(event) => {
            send(event.target.files)
            event.target.value = ''
          }}
        />
      </header>

      {upload.isPending ? (
        <p className="text-ink-500 mb-3 font-mono text-[11px]">{t('document.uploading')}</p>
      ) : null}

      {result === null ? null : <UploadReport result={result} onDismiss={() => setResult(null)} />}

      {documents.data === undefined ? (
        <p className="text-ink-500 font-mono text-xs">{t('common.loading')}</p>
      ) : documents.data.length === 0 ? (
        <Empty />
      ) : (
        <ul className="border-line bg-surface divide-line divide-y rounded-md border">
          {documents.data.map((document) => (
            <DocumentRow
              key={document.id}
              document={document}
              projectKey={projectKey}
              onOpen={() => onOpen(document)}
            />
          ))}
        </ul>
      )}

      <p className="text-ink-500 mt-2 text-[11px]">{t('document.dropHint')}</p>
    </section>
  )
}

function Empty() {
  const { t } = useTranslation()

  return (
    <div className="border-line bg-surface text-ink-500 rounded-md border border-dashed px-4 py-10 text-center">
      <Icon name="folder" className="mx-auto mb-2 size-6 opacity-50" />
      <p className="text-[13px]">{t('document.empty')}</p>
    </div>
  )
}

/** What the server took, and what it would not, named. */
function UploadReport({ result, onDismiss }: { result: UploadResult; onDismiss: () => void }) {
  const { t } = useTranslation()

  return (
    <div className="border-line bg-surface-2 mb-3 rounded-md border px-3 py-2 text-[12px]">
      <div className="flex items-start gap-2">
        <p className="text-ink-700 min-w-0 flex-1">
          {t('document.uploaded', { count: result.stored.length })}
        </p>
        <button
          type="button"
          onClick={onDismiss}
          className="text-ink-500 hover:text-ink-900 shrink-0"
          title={t('view.close')}
        >
          <Icon name="close" className="size-3.5" />
        </button>
      </div>

      {result.rejected.length === 0 ? null : (
        <div className="mt-1.5">
          <p className="font-medium text-[color:var(--color-state-blocked)]">
            {t('document.rejected', { count: result.rejected.length })}
          </p>
          <ul className="text-ink-700 mt-1 flex flex-col gap-0.5">
            {result.rejected.map((file: RejectedFile) => (
              <li key={file.name} className="flex flex-wrap gap-x-2">
                <span className="font-mono text-[11px]">{file.name}</span>
                <span className="text-ink-500">{file.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function DocumentRow({
  document: file,
  projectKey,
  onOpen,
}: {
  document: ProjectDocument
  projectKey: string
  onOpen: () => void
}) {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const [renaming, setRenaming] = useState(false)
  const [title, setTitle] = useState(file.name)

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['documents', projectKey] })

  const change = useMutation<ProjectDocument, ApiError, { title?: string | null; visibility?: Visibility }>({
    mutationFn: (changes) => updateDocument(file.id, changes),
    onSuccess: () => {
      setRenaming(false)
      void refresh()
    },
  })

  const remove = useMutation<void, ApiError, void>({
    mutationFn: () => deleteDocument(file.id),
    onSuccess: () => void refresh(),
  })

  const download = useMutation<string, ApiError, void>({
    mutationFn: () => documentUrl(file.id),
    onSuccess: (url) => window.open(url, '_blank', 'noopener'),
  })

  return (
    <li className="hover:bg-surface-2 flex items-center gap-3 px-3 py-2.5">
      <Icon name="file" className="text-ink-500 size-4 shrink-0" />

      <div className="min-w-0 flex-1">
        {renaming ? (
          <form
            onSubmit={(event) => {
              event.preventDefault()
              change.mutate({ title: title.trim() })
            }}
            className="flex items-center gap-1.5"
          >
            <input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              aria-label={t('document.renameTitle')}
              autoFocus
              className="border-line bg-surface min-w-0 flex-1 rounded border px-2 py-1 text-[13px] outline-none focus:border-[color:var(--color-brand-300)]"
            />
            <button
              type="submit"
              className="bg-brand-600 rounded px-2 py-1 text-[11px] text-white"
              disabled={change.isPending}
            >
              {t('document.save')}
            </button>
            <button
              type="button"
              onClick={() => setRenaming(false)}
              className="text-ink-500 px-1.5 py-1 text-[11px]"
            >
              {t('document.cancel')}
            </button>
          </form>
        ) : (
          <button type="button" onClick={onOpen} className="block w-full text-start">
            <span className="text-ink-900 block truncate text-[13px] font-medium">{file.name}</span>
            <span className="text-ink-500 flex flex-wrap items-center gap-x-2 text-[11px]">
              <span className="font-mono">{formatSize(file.sizeBytes, i18n.language)}</span>
              <span>{new Date(file.createdAt).toLocaleDateString(i18n.language)}</span>
              {file.uploadedBy == null ? null : (
                <span className="flex items-center gap-1">
                  <Avatar name={file.uploadedBy.name} color={file.uploadedBy.color} size="sm" />
                  {file.uploadedBy.name}
                </span>
              )}
            </span>
          </button>
        )}
      </div>

      {file.commentCount === undefined || file.commentCount === 0 ? null : (
        <span className="text-ink-500 flex shrink-0 items-center gap-1 text-[11px]">
          <Icon name="comment" className="size-3.5" />
          {file.commentCount}
        </span>
      )}

      <span
        className={`shrink-0 rounded px-1.5 py-px font-mono text-[9px] tracking-wide uppercase ${
          file.visibility === 'client' ? 'bg-brand-50 text-brand-700' : 'bg-surface-2 text-ink-500'
        }`}
      >
        {file.visibility === 'client' ? t('view.clientVisible') : t('view.internal')}
      </span>

      <div className="flex shrink-0 items-center gap-0.5">
        <RowAction
          icon="download"
          label={download.isError ? t('document.downloadFailed') : t('document.download')}
          onClick={() => download.mutate()}
        />
        <RowAction icon="pencil" label={t('document.rename')} onClick={() => setRenaming(true)} />
        <RowAction
          icon="people"
          label={file.visibility === 'client' ? t('document.unshare') : t('document.share')}
          onClick={() =>
            change.mutate({ visibility: file.visibility === 'client' ? 'internal' : 'client' })
          }
        />
        <RowAction
          icon="trash"
          label={t('document.delete')}
          onClick={() => {
            if (window.confirm(t('document.deleteConfirm'))) remove.mutate()
          }}
        />
      </div>
    </li>
  )
}

function RowAction({
  icon,
  label,
  onClick,
}: {
  icon: 'download' | 'pencil' | 'trash' | 'people'
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="text-ink-500 hover:bg-surface hover:text-ink-900 rounded p-1.5"
    >
      <Icon name={icon} className="size-3.5" />
    </button>
  )
}
